import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, Bot, Users, BarChart3 } from "lucide-react";
import { CAPABILITIES } from "@/data/content";
import SectionHeading from "./SectionHeading";

const iconMap = {
    "creative-systems": Sparkles,
    "ai-agent-workflows": Bot,
    "human-talent": Users,
    "growth-engines": BarChart3,
};

export const BentoCapabilities = () => {
    return (
        <section
            id="capabilities"
            className="relative section-radial py-20 lg:py-28"
            aria-labelledby="capabilities-heading"
        >
            <div className="mx-auto max-w-7xl container-px">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
                    <SectionHeading
                        eyebrow="Capabilities"
                        title={
                            <>
                                Four product capabilities,
                                <br className="hidden md:block" />{" "}
                                <span className="italic text-[hsl(var(--text-muted))]">one operating system.</span>
                            </>
                        }
                        subtitle="Each capability is a system you can put to work — not a service line. They’re modular, measurable, and designed to compound when used together."
                    />
                </div>
                <div
                    className="grid grid-cols-1 md:grid-cols-6 gap-4 sm:gap-5 lg:gap-6"
                    id="capabilities-heading"
                >
                    {CAPABILITIES.map((c, i) => {
                        const Icon = iconMap[c.id] || Sparkles;
                        const spans = [
                            "md:col-span-4",
                            "md:col-span-2",
                            "md:col-span-2",
                            "md:col-span-4",
                        ];
                        return (
                            <motion.article
                                key={c.id}
                                initial={{ opacity: 0, y: 14, filter: "blur(6px)" }}
                                whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                                viewport={{ once: true, margin: "-10% 0px" }}
                                transition={{
                                    duration: 0.55,
                                    delay: i * 0.06,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                                whileHover={{ y: -3 }}
                                data-testid="capability-bento-card"
                                className={`group relative overflow-hidden rounded-3xl border border-black/[0.07] bg-white p-6 sm:p-7 lg:p-8 shadow-soft hover:shadow-deep transition-all duration-300 ${spans[i]}`}
                            >
                                {/* Top accent line */}
                                <div
                                    className={`absolute inset-x-0 top-0 h-px ${
                                        c.accent === "gold"
                                            ? "bg-gradient-to-r from-transparent via-[rgba(187,118,33,0.55)] to-transparent"
                                            : "bg-gradient-to-r from-transparent via-[rgba(37,99,235,0.55)] to-transparent"
                                    }`}
                                />
                                <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full pointer-events-none opacity-50 blur-3xl bg-[rgba(37,99,235,0.10)]" />
                                <div className="absolute -bottom-32 -left-20 h-72 w-72 rounded-full pointer-events-none opacity-40 blur-3xl bg-[rgba(187,118,33,0.08)]" />

                                <div className="relative z-10 flex flex-col gap-5 h-full">
                                    <header className="flex items-start justify-between gap-4">
                                        <span className="text-[11px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
                                            {c.eyebrow}
                                        </span>
                                        <span
                                            className={`inline-flex h-9 w-9 items-center justify-center rounded-full ring-1 ${
                                                c.accent === "gold"
                                                    ? "bg-[rgba(187,118,33,0.10)] ring-[rgba(187,118,33,0.35)] text-[hsl(var(--gold))]"
                                                    : "bg-[rgba(37,99,235,0.10)] ring-[rgba(37,99,235,0.35)] text-[hsl(var(--blue))]"
                                            }`}
                                        >
                                            <Icon className="h-4 w-4" />
                                        </span>
                                    </header>
                                    <h3 className="font-display text-3xl lg:text-[36px] leading-[1.05] tracking-[-0.02em] text-[hsl(var(--text))]">
                                        {c.title}
                                    </h3>
                                    <p className="text-[14.5px] leading-[1.65] text-[hsl(var(--text-muted))] max-w-[52ch]">
                                        {c.kicker}
                                    </p>
                                    <ul className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-[13.5px] text-[hsl(var(--text-muted))]">
                                        {c.bullets.map((b) => (
                                            <li key={b} className="flex items-start gap-2">
                                                <span
                                                    className={`mt-2 h-1 w-1 rounded-full ${
                                                        c.accent === "gold"
                                                            ? "bg-[hsl(var(--gold))]"
                                                            : "bg-[hsl(var(--blue))]"
                                                    }`}
                                                />
                                                {b}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="mt-auto pt-4 inline-flex items-center gap-1.5 text-[13px] text-[hsl(var(--text-muted))] group-hover:text-[hsl(var(--text))] transition-colors">
                                        Learn more
                                        <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                    </div>
                                </div>
                            </motion.article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default BentoCapabilities;
